import type { CardType } from "@mn/shared";

/** Crisp line icons for the toolbar and the guide (20x20 viewBox, currentColor). */
const P = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

export const CARD_ICONS: Partial<
  Record<CardType | "select" | "hand", (size?: number) => React.ReactElement>
> = {
  select: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M4 3l12 7-5 1.5L8.5 17 4 3z" fill="currentColor" stroke="none" />
    </svg>
  ),
  hand: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M7 10V4.5a1 1 0 012 0V9m0-5.5a1 1 0 012 0V9m0-4a1 1 0 012 0v5m0-3a1 1 0 012 0v5c0 3.5-2.5 5.5-5.5 5.5S5 15.5 5 13l-1.5-3a1 1 0 011.7-1L7 11" />
    </svg>
  ),
  note: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M4 3h12v9l-4 4H4V3z" />
      <path d="M12 16v-4h4M7 7h6M7 10h4" />
    </svg>
  ),
  heading: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M4 4h12M10 4v12M7 16h6" strokeWidth={2} />
    </svg>
  ),
  image: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <rect x="3" y="4" width="14" height="12" rx="1.5" />
      <circle cx="7.5" cy="8" r="1.5" />
      <path d="M3 14l4-4 3 3 2.5-2.5L17 14" />
    </svg>
  ),
  link: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M8.5 11.5l3-3M7 13l-1.5 1.5a2.5 2.5 0 01-3.5-3.5L5 8M13 7l1.5-1.5a2.5 2.5 0 013.5 3.5L15 12" />
    </svg>
  ),
  board: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <rect x="3" y="3" width="14" height="14" rx="3" />
      <path d="M7 10h6M10 7v6" />
    </svg>
  ),
  column: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <rect x="4" y="3" width="12" height="14" rx="1.5" />
      <path d="M4 7h12M7 10.5h6M7 13.5h6" />
    </svg>
  ),
  todo: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <rect x="3" y="4" width="5" height="5" rx="1" />
      <path d="M4.2 6.6l1.2 1.2 2-2.4M11 6.5h6M11 13.5h6" />
      <rect x="3" y="11" width="5" height="5" rx="1" />
    </svg>
  ),
  comment: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M3 5a2 2 0 012-2h10a2 2 0 012 2v6a2 2 0 01-2 2H9l-4 3v-3H5a2 2 0 01-2-2V5z" />
      <path d="M7 7h6M7 10h4" />
    </svg>
  ),
  line: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M4 16L16 4M10 4h6v6" />
    </svg>
  ),
  swatch: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <circle cx="10" cy="10" r="6.5" />
      <path d="M10 3.5a6.5 6.5 0 010 13z" fill="currentColor" stroke="none" />
    </svg>
  ),
  sketch: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M3 17l1-4L13.5 3.5a1.5 1.5 0 012.1 0l.9.9a1.5 1.5 0 010 2.1L7 16l-4 1z" />
      <path d="M12 5l3 3" />
    </svg>
  ),
  annotation: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M3 14c3-6 6 2 9-4s4 6 5 2" />
      <path d="M15 3l2 2-6 6-3 1 1-3 6-6z" fill="currentColor" stroke="none" />
    </svg>
  ),
  table: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <rect x="3" y="4" width="14" height="12" rx="1.5" />
      <path d="M3 8h14M3 12h14M8 4v12M13 4v12" />
    </svg>
  ),
  document: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M5 3h7l4 4v10H5V3z" />
      <path d="M12 3v4h4M7.5 10h5M7.5 13h5" />
    </svg>
  ),
  video: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <rect x="3" y="5" width="14" height="10" rx="1.5" />
      <path d="M8.5 7.5v5l4-2.5-4-2.5z" fill="currentColor" stroke="none" />
    </svg>
  ),
  audio: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M8 14.5V5l8-1.5v9.5" />
      <circle cx="6" cy="14.5" r="2" />
      <circle cx="14" cy="13" r="2" />
    </svg>
  ),
  map: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M10 17s-5-4.5-5-8.5a5 5 0 0110 0C15 12.5 10 17 10 17z" />
      <circle cx="10" cy="8.5" r="1.8" />
    </svg>
  ),
  file: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 20 20" {...P} aria-hidden>
      <path d="M5 3h7l4 4v10H5V3z" />
      <path d="M12 3v4h4" />
      <path d="M8 13.5v-3a2 2 0 014 0v3.5a1.5 1.5 0 01-3 0v-3" />
    </svg>
  ),
};

export const cardIcon = (type: CardType | "select" | "hand", size = 18) => CARD_ICONS[type]?.(size) ?? null;
